import React, { useEffect, useRef } from 'react';
import { motion, useInView, useAnimation, AnimatePresence } from 'framer-motion';
import { LearningSpaceSection as LearningSpaceType } from '../../../types';

interface LearningSpaceProps {
  data?: LearningSpaceType;
  fallbackFeatures?: Array<{ icon: string; text: string }>;
}

// Fallback data when API response is null or invalid
const DEFAULT_LEARNING_SPACE_DATA: LearningSpaceType = {
  title: 'Không Gian Học Tập Hiện Đại',
  description: 'Truy cập nền tảng học tập tiên tiến của chúng tôi được thiết kế cho sự thành công của bạn',
  image: '/learning-space.jpg',
};

// Features list for fallback
const DEFAULT_FEATURES = [
  { icon: 'video', text: 'Các lớp tương tác trực tiếp' },
  { icon: 'laptop', text: 'Nền tảng học tập trực tuyến' },
  { icon: 'headphones', text: 'Hướng dẫn cá nhân hóa' },
  { icon: 'clock', text: 'Truy cập học tập 24/7' },
];

const LearningSpace: React.FC<LearningSpaceProps> = ({ data, fallbackFeatures }) => {
  // Refs and animation setup
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, amount: 0.3 });
  const controls = useAnimation();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  // Auto-rotate carousel every 5 seconds
  useEffect(() => {
    const spaceData = data || DEFAULT_LEARNING_SPACE_DATA;
    const images = spaceData.images && spaceData.images.length > 0 ? spaceData.images : [];
    
    if (images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [data]);

  // Use fallback if data is null or undefined
  const spaceData = data || DEFAULT_LEARNING_SPACE_DATA;
  const features = (spaceData.features && spaceData.features.length > 0)
    ? spaceData.features
    : (fallbackFeatures || DEFAULT_FEATURES);

  // Animation variants
  const containerVariants = {
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 80,
        damping: 15,
      },
    },
  };

  return (
    <section
      ref={sectionRef}
      className="py-16 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800"
      id="learning-space"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left - Image Carousel */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="relative rounded-xl shadow-2xl w-full h-96 overflow-hidden bg-gray-200 group">
              {/* Images */}
              {spaceData.images && spaceData.images.length > 0 ? (
                <>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentImageIndex}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="w-full h-full"
                    >
                      <img
                        src={spaceData.images[currentImageIndex]?.image}
                        alt={`Learning Space ${currentImageIndex + 1}`}
                        className="rounded-xl w-full h-full object-cover"
                      />
                    </motion.div>
                  </AnimatePresence>

                  {/* Navigation Buttons */}
                  {spaceData.images.length > 1 && (
                    <>
                      <button
                        onClick={() =>
                          setCurrentImageIndex(
                            (prev) =>
                              (prev - 1 + spaceData.images.length) %
                              spaceData.images.length
                          )
                        }
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white text-gray-800 rounded-full w-10 h-10 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 shadow-lg"
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>

                      <button
                        onClick={() =>
                          setCurrentImageIndex(
                            (prev) => (prev + 1) % spaceData.images.length
                          )
                        }
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white text-gray-800 rounded-full w-10 h-10 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 shadow-lg"
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>

                      {/* Dot Indicators */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
                        {spaceData.images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              index === currentImageIndex
                                ? 'bg-white w-6'
                                : 'bg-white/50 hover:bg-white/70'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : spaceData.image ? (
                <img
                  src={spaceData.image}
                  alt="Learning Space"
                  className="rounded-xl shadow-2xl w-full object-cover h-full"
                />
              ) : (
                <motion.div
                  className="rounded-xl shadow-2xl w-full h-full bg-gradient-to-br from-[#017e6e] via-[#01a798] to-emerald-400 flex items-center justify-center"
                  whileHover={{ scale: 1.02 }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-32 h-32 text-white opacity-40"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M12 3v1m6.364 1.636l-.707-.707M21 12h-1m1.364 6.364l-.707-.707M12 21v1m-6.364-1.636l.707.707M3 12h1M3.636 5.636l.707.707M7 12a5 5 0 1110 0 5 5 0 01-10 0z"
                    />
                  </svg>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Title with animated underline */}
            <div className="mb-6">
              <motion.h2
                className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 text-gray-900 dark:text-white"
              >
                {spaceData.title}
              </motion.h2>
              <motion.div
                className="h-1 w-20 bg-gradient-to-r from-[#017e6e] to-emerald-400"
                initial={{ width: 0 }}
                animate={isInView ? { width: 80 } : {}}
                transition={{ duration: 0.7, delay: 0.3 }}
              ></motion.div>
            </div>

            {/* Description */}
            <motion.p
              className="text-gray-700 dark:text-gray-300 text-base sm:text-lg mb-8 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.3 }}
            >
              {spaceData.description}
            </motion.p>

            {/* Features List with stagger animation */}
            <motion.div
              className="space-y-4 mb-8"
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              variants={containerVariants}
            >
              {features.map((feature, index) => (
                <Feature key={index} icon={feature.icon} text={feature.text} variants={itemVariants} />
              ))}
            </motion.div>

            {/* CTA Button */}
          
          </motion.div>
        </div>
      </div>
    </section>
  );
};

interface FeatureProps {
  icon: string;
  text: string;
  variants: any;
}

const Feature: React.FC<FeatureProps> = ({ icon, text, variants }) => {
  return (
    <motion.div
      className="flex items-center gap-4 group"
      variants={variants}
      whileHover={{ x: 8 }}
    >
      <motion.div
        className="w-12 h-12 bg-gradient-to-br from-[#017e6e]/10 to-emerald-400/10 dark:from-[#017e6e]/20 dark:to-emerald-400/20 rounded-lg flex items-center justify-center text-[#017e6e] dark:text-emerald-400 flex-shrink-0 group-hover:bg-gradient-to-br group-hover:from-[#017e6e] group-hover:to-emerald-400 group-hover:text-white transition-all duration-300"
        whileHover={{ scale: 1.1 }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          {icon === 'video' && (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          )}
          {icon === 'laptop' && (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          )}
          {icon === 'headphones' && (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          )}
          {icon === 'clock' && (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          )}
        </svg>
      </motion.div>
      <span className="text-gray-900 dark:text-gray-100 font-semibold text-base group-hover:text-[#017e6e] dark:group-hover:text-emerald-400 transition-colors">
        {text}
      </span>
    </motion.div>
  );
};

export default LearningSpace;
